import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { Box, Grid, MenuItem, Stack } from "@mui/material";
import { ReportProblemOutlined } from "@mui/icons-material";
import ValidatorSelect from "../../Validator/ValidatorSelect";
import ValidatorTextField from "../../Validator/ValidatorTextField";
import ValidatorDateField from "../../Validator/ValidatorDatePicker";
import LegalbirdAutoComplete from "../../AutoComplete/LegalbirdAutoComplete";
import LegalbirdTipTapEditor from "../../LbEditor/LegalbirdTipTapEditor";
import WorkingHoursHint from "./WorkingHoursHint";
import ActivityHistory from "./ActivityHistory";
import DateChangeButtonsToolbar from "../../Validator/DateChangeButtonsToolbar";
import HolidayAwareDateChangeButtonsToolbar from "../../Validator/HolidayAwareDateChangeButtonsToolbar";
import ApiClient from "../../../services/ApiClient";
import { formValue } from "../../../services/formServiceFunctions";
import {
  deadlineActivityTypes,
  getActivitiesSet,
  getActivityData,
  transformActivitySetToAutoCompleteOptions,
} from "../../../services/Activities/activityTypes";
import { dateFieldDefault, isTodayOrFutureDate, requiredFieldDefault } from "../../../services/validationRules";
import {
  getOperationalRoleToDisplay,
  sortAssignedUsersToTop,
  sortByOperationalRole,
  userHasRole,
} from "../../../services/backofficeUserService";
import { useHolidays } from "../../../provider/HolidayProvider";
import { useBackofficeUser } from "../../../provider/BackofficeUserProvider";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import useForm from "../../../hooks/useForm";
import { BackofficeUser, Roles } from "../../../types/BackofficeUser";
import { BackofficeCase } from "../../../types/BackofficeCase";
import { AutoCompleteOptionWithId } from "../../../types/AutoCompleteOptionWithId";
import editActivityStyles from "./editActivityStyles";
import { setEditorContent } from "../../../services/Editor/editorFunctions";
import { useCurrentEditor } from "@tiptap/react";
import { ActivityFormProps } from "./ActivityForm";
import dayjs, { Dayjs } from "dayjs";

const availabilityInfoTranslations = {
  absence: "Der Nutzer ist an dem Tag nicht anwesend",
  capacityReached: "Der Nutzer ist an dem Tag bereits zeitlich ausgelastet",
  available: "",
};

const isAssignedUser = (user: BackofficeUser, backofficeCase: BackofficeCase | null) => {
  switch (true) {
    case _.isString(backofficeCase?.accountManager) || _.isString(backofficeCase?.lawyer):
      return (
        (backofficeCase?.accountManager as unknown as string) === user["@id"] ||
        (backofficeCase?.lawyer as unknown as string) === user["@id"]
      );
    case _.isObject(backofficeCase?.accountManager) || _.isObject(backofficeCase?.lawyer):
      return _.get(backofficeCase, "accountManager.id") === user.id || _.get(backofficeCase, "lawyer.id") === user.id;
    default:
      return false;
  }
};

const getUsers = (agents: BackofficeUser[], backofficeCase: BackofficeCase | null) => {
  const users = sortAssignedUsersToTop(agents, backofficeCase);
  const userOptions: AutoCompleteOptionWithId[] = _.map(users, (user) => {
    return {
      label: user.person.fullname,
      id: user["@id"],
      orderBy: isAssignedUser(user, backofficeCase) ? "Zuständig" : getOperationalRoleToDisplay(user),
    };
  });
  return [{ label: "Nicht zugeordnet", id: null }, ...userOptions];
};

type ActivityFormInnerProps = {
  formProps: ReturnType<typeof useForm>;
} & Pick<ActivityFormProps, "product" | "productClassName" | "activity" | "open">;

const ActivityFormInner = ({ formProps, product, productClassName, activity, open }: ActivityFormInnerProps) => {
  const currentUser = useCurrentUser();
  const { agents, internals, externals } = useBackofficeUser();
  const { holidays } = useHolidays();
  const { editor } = useCurrentEditor();

  const [userAvailabilityStatus, setUserAvailabilityStatus] = useState("");
  const [mandatorySelectDisabled, setMandatorySelectDisabled] = useState(false);
  const durations = [1, 3, 5, 10, 15, 20, 30, 45, 60, 90, 120, 180];

  const { values, errors, handleChange, handleDateChange, handleBlur, registerValidators, touchedValues } = formProps;

  const fetchAvailabilityStatus = useCallback(
    (assignedUser: string, dueDate: Dayjs | string) => {
      const dueDateDayjs = dayjs(dueDate);
      if (
        !dueDateDayjs.isValid() ||
        !assignedUser ||
        !open ||
        _.find(externals, (external) => external["@id"] === assignedUser)
      ) {
        setUserAvailabilityStatus("");
        return;
      }

      ApiClient.get(
        `services/human_resource_management/user_availability_status/${_.replace(
          assignedUser,
          "/backoffice_users/",
          ""
        )}/${dueDateDayjs.format("YYYY-MM-DD")}`
      ).then((result) => {
        setUserAvailabilityStatus(result.status);
      });
    },
    [currentUser, open]
  );

  const updateMandatoryActivity = (assignedUser: string, dueDate: Dayjs, activityType: string) => {
    if (
      (dayjs().isSame(dueDate, "day") && currentUser["@id"] !== assignedUser) ||
      _.includes(["deadline", "peremptory_term", "court_date"], activityType)
    ) {
      handleChange({ target: { name: "mandatory", value: "true" } });
      setMandatorySelectDisabled(true);
      return;
    }

    if (!activity) {
      handleChange({ target: { name: "mandatory", value: "false" } });
    }
    setMandatorySelectDisabled(false);
  };

  useEffect(() => {
    if (values.duration === undefined || values.duration === null || !durations.includes(values.duration)) {
      handleChange({
        target: {
          name: "duration",
          value: 1,
        },
      });
    }
  }, [values.duration]);

  useEffect(() => {
    fetchAvailabilityStatus(values.assignedUser, values.dueDate);
    updateMandatoryActivity(values.assignedUser, values.dueDate, values.type);
  }, [open]);

  const getRelevantAgentsList = useCallback(() => {
    if (!activity) {
      return internals;
    }

    const assignedUser = agents.find((agent) => agent["@id"] === activity?.assignedUser);
    if (!assignedUser || !userHasRole(assignedUser, Roles.external)) {
      return internals;
    }

    return [...internals, assignedUser];
  }, [activity, product, agents, internals]);

  const users = useMemo(() => {
    return getUsers(
      sortByOperationalRole(getRelevantAgentsList()),
      activity ? activity.case : product?.backofficeCase || null
    );
  }, [activity, product]);

  const activityTypes = useMemo(() => {
    return transformActivitySetToAutoCompleteOptions(getActivitiesSet(productClassName));
  }, [product]);

  const handleUserChange = (e: any, value: AutoCompleteOptionWithId | null) => {
    if (!value) {
      return;
    }
    handleChange({
      target: {
        name: "assignedUser",
        value: value.id,
      },
    });
    updateMandatoryActivity(value.id, values.dueDate, values.type);
    fetchAvailabilityStatus(value.id, values.dueDate);
  };

  const handleActivityTypeValueChange = (event: SyntheticEvent, value: AutoCompleteOptionWithId | null) => {
    if (!value) {
      return;
    }
    //must be before handleChange to get the old value
    const previousActivityType = getActivityData(productClassName, formValue(values, "type"));

    handleChange({
      target: {
        name: "type",
        value: value.id,
      },
    });
    updateMandatoryActivity(values.assignedUser, values.dueDate, value.id);

    const activityData = getActivityData(productClassName, value.id);

    handleChange({
      target: {
        name: "duration",
        value: activityData?.defaultDuration ?? 1,
      },
    });

    if (_.get(activityData, "noteToPrefill")) {
      setEditorContent(editor, _.get(activityData, "noteToPrefill", ""));
    }

    if (
      !touchedValues.subject ||
      _.includes(
        [previousActivityType?.label, _.get(previousActivityType, "descriptionToPrefill"), ""],
        formValue(values, "subject")
      )
    ) {
      const changeDescriptionEvent = {
        target: {
          name: "subject",
          value: _.get(activityData, "prefillDescription")
            ? _.get(activityData, "descriptionToPrefill", _.get(activityData, "label", "N/A"))
            : "",
        },
      };
      handleChange(changeDescriptionEvent);
    }
  };

  const handleDateChangeWrapped = (date: Dayjs | null) => {
    if (!date) {
      return;
    }

    //to prevent the user from entering the year 0 which isn't accepted by the backend
    //we cant validate it on submit because fetchAvailabilityStatus is executed here and on assigned user change and thus uses the value pre validation
    if (date.year() <= 0) {
      handleDateChange(dayjs(), "dueDate");
      return;
    }

    handleDateChange(date, "dueDate");
    updateMandatoryActivity(values.assignedUser, date, values.type);
    fetchAvailabilityStatus(values.assignedUser, date);
  };

  const getActionsInfo = () => {
    if (_.includes(holidays, dayjs(values.dueDate).format("YYYY-MM-DD"))) {
      return (
        <Box sx={editActivityStyles.availabilityStatus}>
          <Stack
            direction={"row"}
            sx={{
              alignItems: "center",
              justifyContent: "center"
            }}>
            <ReportProblemOutlined />
            Dieser Tag ist ein Feiertag
          </Stack>
        </Box>
      );
    }
    if (!_.includes(["", "available"], userAvailabilityStatus)) {
      return (
        <Box sx={editActivityStyles.availabilityStatus}>
          <Stack
            direction={"row"}
            sx={{
              alignItems: "center",
              justifyContent: "center"
            }}>
            <ReportProblemOutlined />
            {availabilityInfoTranslations[userAvailabilityStatus as keyof typeof availabilityInfoTranslations]}
          </Stack>
        </Box>
      );
    } else if (!!values.assignedUser) {
      return <WorkingHoursHint dueDate={values.dueDate} dueTime={values.dueTime} assignedUser={values.assignedUser} />;
    }
  };

  const dueDateDisabled = activity && !!(activity.doneTime || activity.dueDate < dayjs().format("YYYY-MM-DD"));
  let dueDateValidators = [...requiredFieldDefault, ...dateFieldDefault];

  if (!dueDateDisabled) {
    dueDateValidators.push({
      validator: isTodayOrFutureDate,
      message: "Es können keine vergangenen Tage ausgewählt werden",
    });
  }

  const isDeadline = deadlineActivityTypes.includes(values?.type);
  const toolbar = isDeadline ? DateChangeButtonsToolbar : HolidayAwareDateChangeButtonsToolbar;

  const userAutoCompleteValue = _.find(users, (user) => user.id === (values.assignedUser || null));
  const activityTypeAutoCompleteValue = _.find(activityTypes, (activityType) => activityType.id === values.type);

  return (
    <>
      {getActionsInfo()}
      <Grid container>
        <Grid
          sx={editActivityStyles.activityGridItem}
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <LegalbirdAutoComplete<AutoCompleteOptionWithId>
            setValue={handleUserChange}
            getOptionDisabled={(option) => option.id === null}
            value={userAutoCompleteValue}
            options={users}
            label={"Zu erledigen durch"}
            disabled={!!activity?.doneTime}
            groupBy={(option) => option.orderBy || ""}
          />
        </Grid>
        <Grid
          sx={editActivityStyles.activityGridItem}
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <LegalbirdAutoComplete<AutoCompleteOptionWithId>
            setValue={handleActivityTypeValueChange}
            getOptionDisabled={(option) => option.disabled || false}
            value={activityTypeAutoCompleteValue}
            options={activityTypes}
            label={"Aktivitätstyp"}
            disabled={!!activity?.doneTime}
          />
        </Grid>
        <Grid sx={editActivityStyles.activityGridItem} size={12}>
          <ValidatorTextField
            label={"Beschreibung"}
            name={"subject"}
            value={formValue(values, "subject")}
            onChange={handleChange}
            onBlur={handleBlur}
            registerValidators={registerValidators}
            validators={requiredFieldDefault}
            error={!!errors["subject"]}
            helperText={errors["subject"]}
            slotProps={{
              htmlInput: {
                maxLength: 100,
              },
            }}
            isHighlighted={false}
            initialDependentValidationFields={[]}
          />
        </Grid>
        <Grid
          sx={editActivityStyles.activityGridItem}
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <ValidatorDateField
            format="DD.MM.YYYY"
            label={"Datum"}
            name={"dueDate"}
            disabled={dueDateDisabled}
            shouldDisableDate={(date) => {
              return date.day() === 0 || date.day() === 6;
            }}
            onChange={handleDateChangeWrapped}
            handleBlur={handleBlur}
            value={formValue(values, "dueDate")}
            helperText={errors["dueDate"]}
            registerValidators={registerValidators}
            validators={dueDateValidators}
            disablePast
            slotProps={{
              toolbar: {
                hidden: false,
              },
            }}
            toolbar={toolbar}
          />
        </Grid>
        <Grid
          sx={editActivityStyles.activityGridItem}
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <ValidatorTextField
            label={"Uhrzeit"}
            onChange={handleChange}
            onBlur={handleBlur}
            slotProps={{
              inputLabel: {
                shrink: true
              },
            }}
            type={"time"}
            name={"dueTime"}
            disabled={!!activity?.doneTime}
            value={formValue(values, "dueTime")}
            registerValidators={registerValidators}
            error={!!errors["dueTime"]}
            helperText={errors["dueTime"]}
            validators={[]}
            isHighlighted={false}
            initialDependentValidationFields={[]}
          />
        </Grid>
        <Grid
          sx={editActivityStyles.activityGridItem}
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <ValidatorSelect
            label={"Pflicht?"}
            name={"mandatory"}
            value={formValue(values, "mandatory")}
            onChange={handleChange}
            disabled={mandatorySelectDisabled}
          >
            <MenuItem value={"true"}>Pflicht</MenuItem>
            <MenuItem value={"false"}>Keine Pflicht</MenuItem>
          </ValidatorSelect>
        </Grid>
        <Grid
          sx={editActivityStyles.activityGridItem}
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <ValidatorSelect
            label={"Dauer"}
            name={"duration"}
            // "/backoffice_users/13" is the system user
            disabled={activity && !!(activity.creator === "/backoffice_users/13" || activity.doneTime)}
            value={formValue(values, "duration")}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors["duration"]}
            helperText={errors["duration"]}
          >
            {_.map(durations, (duration) => (
              <MenuItem key={duration} value={duration}>
                {duration} {duration === 1 ? "Minute" : "Minuten"}
              </MenuItem>
            ))}
          </ValidatorSelect>
        </Grid>
        <Grid sx={editActivityStyles.activityGridItem} size={12}>
          <LegalbirdTipTapEditor editorSx={editActivityStyles.editorContent} sx={editActivityStyles.editor} />
        </Grid>
      </Grid>
      {activity && <ActivityHistory activity={activity} productClassName={productClassName} />}
    </>
  );
};

export default ActivityFormInner;
